import { createAsyncThunk, Unsubscribe } from "@reduxjs/toolkit";
import { Collaboration } from "src/app/models/Chat.v2";
import { Timestamp, onSnapshot, QuerySnapshot, DocumentData, QueryDocumentSnapshot } from "firebase/firestore";
import db from "src/db";
import * as chatSlice from "../../../modules/chat/redux/ChatSlice.v2";
import Message from "src/app/models/Message";
import * as lc from "src/app/modules/localstorage/index";
import { createDocRef } from "src/app/components/list/hook/useFirestoreData";
import { Account } from "src/app/models/Account";
import { messageApiClient, ShopeeMessageHelper, LazadaMessageHelper } from "../services";
import {
  getChatListCursorFromData,
  mergeChatListForStorage,
  readStoredChatList,
  writeStoredChatList,
} from "../services/chatListStorageCache";
import {
  getMessageCacheCursorFromData,
  mergeMessagesForStorage,
  readStoredMessages,
  writeStoredMessages,
} from "../services/messageStorageCache";
import {
  auditChatRead,
  auditDocIds,
  countDocChangesByType,
} from "../utils/firestoreReadAudit";
import {
  CHAT_INITIAL_WINDOW_DAYS,
  CHAT_LIST_PAGE_SIZE,
  MESSAGE_INITIAL_PAGE_SIZE,
  NEWER_MESSAGES_PAGE_SIZE,
} from "../config/queryLimits";

type ChatType = "allChat" | "myChat";
type ChatListQueryMode = "initial" | "refresh" | "pagination";

const getClientRef = () => {
  const id_client = lc.getItemLC(lc.LCName.Client)?.id;
  return createDocRef("clients", id_client);
};

const toFirestoreTimestamp = (value?: any) => {
  if (!value) return null;
  if (typeof value.toMillis === "function") {
    return Timestamp.fromDate(new Date(value.toMillis()));
  }
  if (typeof value.seconds === "number") {
    const millis = value.seconds * 1000 + Math.floor((value.nanoseconds ?? 0) / 1000000);
    return Timestamp.fromDate(new Date(millis));
  }
  return null;
};

export const fetchChatList = createAsyncThunk(
  "chat/fetchChatList",
  async (
    {
      chatType,
      lastInteractionAt,
      queryMode = lastInteractionAt ? "pagination" : "initial",
      newestKnownLastInteractionAt,
    }: {
      chatType: ChatType;
      lastInteractionAt?: Timestamp | null;
      queryMode?: ChatListQueryMode;
      newestKnownLastInteractionAt?: Timestamp | null;
    },
    { dispatch, getState }
  ) => {
    dispatch(chatSlice.setLoading({ chatType, isLoading: true }));

    try {
      const state = getState() as { ChatV2?: Record<string, any> };
      const chatState = state.ChatV2?.[chatType] ?? {};
      const cachedList =
        queryMode === "initial" ? readStoredChatList(chatType) : null;
      const effectiveQueryMode =
        queryMode === "initial" && cachedList ? "refresh" : queryMode;
      const cachedNewestLastInteractionAt =
        cachedList?.newestKnownLastInteractionAt
          ? Timestamp.fromDate(
              new Date(cachedList.newestKnownLastInteractionAt.toMillis())
            )
          : null;

      if (cachedList) {
        auditChatRead({
          event: "chat_list_fetch",
          source: "chat_list_storage_hit",
          chatType,
          docs: cachedList.collaborations.length,
          docIds: auditDocIds(cachedList.collaborations.map((item) => item.id)),
          estimatedReads: 0,
          details: {
            cacheState: "localstorage_hit",
            fetchedAt: cachedList.fetchedAt,
          },
        });

        dispatch(chatSlice.setCollaborations({
          chatType,
          data: cachedList.collaborations,
        }));
        dispatch(chatSlice.setChatListCursor({
          chatType,
          newestKnownLastInteractionAt: cachedList.newestKnownLastInteractionAt,
          oldestVisibleLastInteractionAt: cachedList.oldestVisibleLastInteractionAt,
          lastFetchedAt: cachedList.fetchedAt,
        }));
        dispatch(chatSlice.setHasMore({
          chatType,
          hasMore: cachedList.collaborations.length === CHAT_LIST_PAGE_SIZE,
        }));
      }

      const query = buildChatQuery({
        chatType,
        queryMode: effectiveQueryMode,
        lastInteractionAt,
        newestKnownLastInteractionAt:
          newestKnownLastInteractionAt ??
          cachedNewestLastInteractionAt ??
          chatState.newestKnownLastInteractionAt ??
          null,
      });
      const startedAt = Date.now();
      const snap =
        (await query.get()) as unknown as QuerySnapshot<DocumentData>;
      const source =
        effectiveQueryMode === "pagination"
          ? "chat_list_pagination"
          : effectiveQueryMode === "refresh"
          ? "chat_list_refresh"
          : "chat_list_fetch";

      auditChatRead({
        event: "chat_list_fetch",
        source,
        chatType,
        docs: snap.docs.length,
        docIds: auditDocIds(snap.docs.map((doc) => doc.id)),
        estimatedReads: snap.docs.length,
        elapsedMs: Date.now() - startedAt,
        details: {
          queryMode: effectiveQueryMode,
          usedStoredList: Boolean(cachedList),
        },
      });

      const collaborations = await parseCollaborationSnapshot(snap);

      if (effectiveQueryMode === "pagination" || effectiveQueryMode === "refresh") {
        dispatch(
          chatSlice.appendCollaborations({ chatType, data: collaborations })
        );
      } else {
        dispatch(
          chatSlice.setCollaborations({ chatType, data: collaborations })
        );
      }

      if (queryMode === "initial") {
        writeStoredChatList(
          chatType,
          cachedList
            ? mergeChatListForStorage(cachedList.collaborations, collaborations)
            : collaborations
        );
      }

      dispatch(
        chatSlice.setChatListCursor({
          chatType,
          ...(snap.docs.length
            ? buildChatListCursor(snap)
            : cachedList
            ? getChatListCursorFromData(cachedList.collaborations)
            : buildChatListCursor(snap)),
          lastFetchedAt: Date.now(),
        })
      );

      if (effectiveQueryMode !== "refresh") {
        dispatch(
          chatSlice.setHasMore({
            chatType,
            hasMore: snap.docs.length === CHAT_LIST_PAGE_SIZE,
          })
        );
      }
    } catch (err) {
      console.error("❌ Error fetching chat list", chatType, err);
      dispatch(chatSlice.setHasMore({ chatType, hasMore: false }));
    } finally {
      dispatch(chatSlice.setLoading({ chatType, isLoading: false }));
    }
  }
);

export const subscribeToChatList = (
  chatType: ChatType,
  onUpdate: (
    data: Collaboration,
    type: "added" | "modified" | "removed"
  ) => void,
  options: {
    newestKnownLastInteractionAt?: any;
  } = {}
): Unsubscribe => {
  const newestKnownLastInteractionAt = toFirestoreTimestamp(
    options.newestKnownLastInteractionAt
  );
  const usesNewestCursor = Boolean(newestKnownLastInteractionAt);
  const query = buildChatQuery({
    chatType,
    queryMode: usesNewestCursor ? "refresh" : "initial",
    newestKnownLastInteractionAt,
  });
  let isInitialSnapshot = true;

  return onSnapshot(query, async (snapshot) => {
    const startedAt = Date.now();
    const changes = snapshot.docChanges();
    const source = isInitialSnapshot
      ? usesNewestCursor
        ? "chat_list_listener_newer_initial"
        : "chat_list_listener_initial"
      : "chat_list_listener_update";
    const changeCounts = countDocChangesByType(changes);

    auditChatRead({
      event: "chat_list_listener",
      source,
      chatType,
      docs: isInitialSnapshot ? snapshot.docs.length : changes.length,
      docIds: auditDocIds(snapshot.docs.map((doc) => doc.id)),
      changedIds: auditDocIds(changes.map((change) => change.doc.id)),
      estimatedReads: isInitialSnapshot ? snapshot.docs.length : changes.length,
      elapsedMs: Date.now() - startedAt,
      details: changeCounts,
    });

    for (const change of changes) {
      const doc = change.doc;
      const data = await parseSingleCollaboration(doc);
      onUpdate(data, change?.type);
    }

    isInitialSnapshot = false;
  });
};

const buildChatQuery = ({
  chatType,
  queryMode,
  lastInteractionAt,
  newestKnownLastInteractionAt,
}: {
  chatType: ChatType;
  queryMode: ChatListQueryMode;
  lastInteractionAt?: Timestamp | null;
  newestKnownLastInteractionAt?: Timestamp | null;
}) => {
  const threeDaysAgo = Timestamp.fromDate(
    new Date(Date.now() - CHAT_INITIAL_WINDOW_DAYS * 24 * 60 * 60 * 1000)
  );

  const statusFilter = chatType === "myChat" ? "handled" : "queue";

  let query = db
    .collection("collaborations")
    .where("client", "==", getClientRef())
    .where("isActive", "==", true)
    .where("status", "==", statusFilter);

  if (queryMode === "refresh" && newestKnownLastInteractionAt) {
    query = query.where("lastInteractionAt", ">", newestKnownLastInteractionAt);
  } else {
    query = query.where("lastInteractionAt", ">=", threeDaysAgo);
  }

  query = query.orderBy("lastInteractionAt", "desc").orderBy("unreadCount", "desc");

  if (queryMode === "pagination" && lastInteractionAt) {
    query = query.startAfter(lastInteractionAt);
  }

  return query.limit(CHAT_LIST_PAGE_SIZE);
};

const buildChatListCursor = (snap: QuerySnapshot<DocumentData>) => {
  const first = snap.docs[0]?.data()?.lastInteractionAt;
  const last = snap.docs[snap.docs.length - 1]?.data()?.lastInteractionAt;

  return {
    newestKnownLastInteractionAt: first,
    oldestVisibleLastInteractionAt: last,
  };
};

const parseCollaborationSnapshot = async (
  snap: QuerySnapshot<DocumentData>
): Promise<Collaboration[]> => {
  const results: Collaboration[] = [];

  for (const doc of snap.docs) {
    const data = await parseSingleCollaboration(doc);
    results.push(data);
  }

  return results;
};

const parseSingleCollaboration = async (
  doc: QueryDocumentSnapshot<DocumentData>
): Promise<Collaboration> => {
  const data = { ...doc.data(), id: doc.id } as Collaboration;
  return data;
};

export const fetchMessagesForRoom = createAsyncThunk(
  "chat/fetchMessagesForRoom",
  async (
    {
      collaborationId,
      roomId,
      conversationId,
    }: { collaborationId: string; roomId: string; conversationId: string },
    { dispatch }
  ) => {
    dispatch(
      chatSlice.setMessagesLoading({ collaborationId, roomId, isLoading: true })
    );

    try {
      const cached = readStoredMessages(collaborationId, roomId);
      const cachedNewest = toFirestoreTimestamp(cached?.newestCreatedAt);
      const cachedOldest = toFirestoreTimestamp(cached?.oldestCreatedAt);

      if (cached) {
        auditChatRead({
          event: "messages_fetch",
          source: "message_storage_hit",
          collaborationId,
          roomId,
          conversationId,
          docs: cached.messages.length,
          docIds: auditDocIds(cached.messages.map((message) => message.id ?? "")),
          estimatedReads: 0,
          details: { cacheState: "localstorage_hit", fetchedAt: cached.fetchedAt },
        });
        dispatch(chatSlice.setMessagesForRoom({
          collaborationId,
          roomId,
          messages: cached.messages,
          hasMore: cached.messages.length === MESSAGE_INITIAL_PAGE_SIZE,
          lastVisible: cachedOldest,
        }));
      }

      const query = cachedNewest
        ? buildRoomMessagesQuery({
            collaborationId,
            roomId,
            conversationId,
            afterCreatedAt: cachedNewest,
            limitSize: NEWER_MESSAGES_PAGE_SIZE,
          })
        : buildRoomMessagesQuery({ collaborationId, roomId, conversationId });
      const startedAt = Date.now();
      const snap = await query.get();

      auditChatRead({
        event: "messages_fetch",
        source: cached ? "room_select_newer_messages" : "room_select_or_refresh",
        collaborationId,
        roomId,
        conversationId,
        docs: snap.docs.length,
        docIds: auditDocIds(snap.docs.map((doc) => doc.id)),
        estimatedReads: snap.docs.length,
        elapsedMs: Date.now() - startedAt,
      });

      const messages = snap.docs.reverse().map((doc) => {
        return { ...doc.data(), id: doc.id } as Message;
      });
      const mergedMessages = cached
        ? mergeMessagesForStorage(cached.messages, messages)
        : messages;
      const cursor = getMessageCacheCursorFromData(mergedMessages);

      writeStoredMessages(collaborationId, roomId, mergedMessages);

      dispatch(
        chatSlice.setMessagesForRoom({
          collaborationId,
          roomId,
          messages: mergedMessages,
          hasMore: cached
            ? mergedMessages.length === MESSAGE_INITIAL_PAGE_SIZE
            : snap.docs.length === MESSAGE_INITIAL_PAGE_SIZE,
          lastVisible: cached ? toFirestoreTimestamp(cursor.oldestCreatedAt) : snap.docs[snap.docs.length - 1],
        })
      );
    } catch (error) {
      console.error("❌ Error fetching messages", error);
      dispatch(
        chatSlice.setMessagesForRoom({
          collaborationId,
          roomId,
          messages: [],
          hasMore: false,
        })
      );
    } finally {
      dispatch(
        chatSlice.setMessagesLoading({
          collaborationId,
          roomId,
          isLoading: false,
        })
      );
    }
  }
);

export const fetchNewMessagesForRoom = createAsyncThunk(
  "chat/fetchNewMessagesForRoom",
  async (
    {
      collaborationId,
      roomId,
      conversationId,
      afterCreatedAt,
    }: {
      collaborationId: string;
      roomId: string;
      conversationId: string;
      afterCreatedAt: any;
    },
    { dispatch }
  ) => {
    if (!afterCreatedAt) {
      dispatch(
        fetchMessagesForRoom({
          collaborationId,
          roomId,
          conversationId,
        })
      );
      return;
    }

    try {
      const query = buildRoomMessagesQuery({
        collaborationId,
        roomId,
        conversationId,
        afterCreatedAt,
        limitSize: NEWER_MESSAGES_PAGE_SIZE,
      });
      const startedAt = Date.now();
      const snap = await query.get();

      auditChatRead({
        event: "messages_fetch",
        source: "visibility_new_messages",
        collaborationId,
        roomId,
        conversationId,
        docs: snap.docs.length,
        docIds: auditDocIds(snap.docs.map((doc) => doc.id)),
        estimatedReads: snap.docs.length,
        elapsedMs: Date.now() - startedAt,
      });

      snap.docs.reverse().forEach((doc) => {
        dispatch(
          chatSlice.addNewMessage({
            collaborationId,
            roomId,
            message: { ...doc.data(), id: doc.id } as Message,
          })
        );
      });
    } catch (error) {
      console.error("❌ Error fetching new messages", error);
    }
  }
);

export const fetchOlderMessagesForRoom = createAsyncThunk(
  "chat/fetchOlderMessagesForRoom",
  async (
    {
      collaborationId,
      roomId,
      conversationId,
      lastVisible,
    }: {
      collaborationId: string;
      roomId: string;
      conversationId: string;
      lastVisible: any;
    },
    { dispatch }
  ) => {
    dispatch(
      chatSlice.setMessagesLoading({ collaborationId, roomId, isLoading: true })
    );

    try {
      console.log('[THUNK-FETCH-OLDER] Building query with:', { collaborationId, roomId, conversationId, lastVisibleId: lastVisible?.id || 'null' });

      const query = buildRoomMessagesQuery({
        collaborationId,
        roomId,
        conversationId,
        lastVisible,
      });
      const startedAt = Date.now();
      const snap = await query.get();

      auditChatRead({
        event: "messages_fetch",
        source: "message_pagination",
        collaborationId,
        roomId,
        conversationId,
        docs: snap.docs.length,
        docIds: auditDocIds(snap.docs.map((doc) => doc.id)),
        estimatedReads: snap.docs.length,
        elapsedMs: Date.now() - startedAt,
      });

      const messages = snap.docs.reverse().map((doc) => {
        return { ...doc.data(), id: doc.id } as Message;
      });

      dispatch(
        chatSlice.appendOlderMessages({
          collaborationId,
          roomId,
          messages,
          hasMore: snap.docs.length === MESSAGE_INITIAL_PAGE_SIZE,
          lastVisible: snap.docs[snap.docs.length - 1],
        })
      );
    } catch (error) {
      console.error("❌ Error fetching older messages", error);
    } finally {
      dispatch(
        chatSlice.setMessagesLoading({
          collaborationId,
          roomId,
          isLoading: false,
        })
      );
    }
  }
);

export const subscribeToMessagesForRoom = (
  collaborationId: string,
  roomId: string,
  conversationId: string,
  onChange: (message: Message, type: "added" | "modified" | "removed") => void,
  onInitialSnapshot?: (data: {
    messages: Message[];
    lastVisible?: QueryDocumentSnapshot<DocumentData> | any;
    hasMore: boolean;
  }) => void,
  afterCreatedAt?: any
): Unsubscribe => {
  const query = buildRoomMessagesQuery({
    collaborationId,
    roomId,
    conversationId,
    afterCreatedAt,
    limitSize: afterCreatedAt ? NEWER_MESSAGES_PAGE_SIZE : MESSAGE_INITIAL_PAGE_SIZE,
  });
  let isInitialSnapshot = true;

  return onSnapshot(query, (snap) => {
    const changes = snap.docChanges();
    const source = isInitialSnapshot
      ? afterCreatedAt
        ? "messages_listener_newer_initial"
        : "messages_listener_initial"
      : "messages_listener_update";
    const changeCounts = countDocChangesByType(changes);

    auditChatRead({
      event: "messages_listener",
      source,
      collaborationId,
      roomId,
      conversationId,
      docs: isInitialSnapshot ? snap.docs.length : changes.length,
      docIds: auditDocIds(snap.docs.map((doc) => doc.id)),
      changedIds: auditDocIds(changes.map((change) => change.doc.id)),
      estimatedReads: isInitialSnapshot ? snap.docs.length : changes.length,
      details: changeCounts,
    });

    if (isInitialSnapshot && onInitialSnapshot) {
      const initialMessages = snap.docs
        .slice()
        .reverse()
        .map((doc) => ({ ...doc.data(), id: doc.id } as Message));

      if (initialMessages.length && !afterCreatedAt) {
        writeStoredMessages(collaborationId, roomId, initialMessages);
      }

      onInitialSnapshot({
        messages: initialMessages,
        lastVisible: afterCreatedAt
          ? toFirestoreTimestamp(getMessageCacheCursorFromData(initialMessages).oldestCreatedAt)
          : snap.docs[snap.docs.length - 1],
        hasMore: afterCreatedAt
          ? initialMessages.length === NEWER_MESSAGES_PAGE_SIZE
          : snap.docs.length === MESSAGE_INITIAL_PAGE_SIZE,
      });
      isInitialSnapshot = false;
      return;
    }

    snap
      .docChanges()
      .reverse()
      .forEach((change) => {
        const message = {
          ...change.doc.data(),
          id: change.doc.id,
        } as Message;

        if (
          change.type === "added" ||
          change.type === "modified" ||
          change.type === "removed"
        ) {
          onChange(message, change.type);
        }
      });

    isInitialSnapshot = false;
  });
};

const buildRoomMessagesQuery = ({
  collaborationId,
  roomId,
  conversationId,
  afterCreatedAt,
  lastVisible,
  limitSize = MESSAGE_INITIAL_PAGE_SIZE,
}: {
  collaborationId: string;
  roomId: string;
  conversationId: string;
  afterCreatedAt?: any;
  lastVisible?: any;
  limitSize?: number;
}) => {
  const roomRef = db.doc(`collaborations/${collaborationId}/rooms/${roomId}`);
  let ref = roomRef
    .collection("messages")
    .where("client", "==", getClientRef())
    .where("isActive", "==", true);

  if (afterCreatedAt) {
    ref = ref.where("createdAt", ">", afterCreatedAt);
  }

  ref = ref.orderBy("createdAt", "desc");

  if (lastVisible) {
    ref = ref.startAfter(lastVisible);
  }

  return ref.limit(limitSize);
};

/**
 * Send marketplace message via unified API
 * Supports: Shopee (now), Lazada (future), TikTok (future)
 * Server creates the message in Firestore - client just triggers the send
 */
export const sendMarketplaceMessage = createAsyncThunk(
  "chat/sendMarketplaceMessage",
  async (
    {
      platform,
      message,
      account,
      recipientId,
    }: {
      platform: "shopee" | "lazada" | "tiktok";
      message: Message;
      account: Account;
      recipientId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      // Validate account has marketplaceShopId
      if (!account.marketplaceShopId) {
        throw new Error(
          `Account does not have marketplaceShopId configured for ${platform}`
        );
      }

      // Platform-specific validation and formatting
      let requestBody;
      switch (platform) {
        case "shopee":
          // Use Shopee helper for validation
          ShopeeMessageHelper.validate(message, recipientId);
          // Build Shopee request body
          requestBody = ShopeeMessageHelper.buildRequestBody(recipientId, message);
          break;

        case "lazada":
          // Use Lazada helper for validation
          LazadaMessageHelper.validate(message, recipientId);
          // Build Lazada request body
          requestBody = LazadaMessageHelper.buildRequestBody(recipientId, message);
          break;

        // Future platforms will add their helpers here
        case "tiktok":
          throw new Error("TikTok support not yet implemented");

        default:
          throw new Error(`Unsupported platform: ${platform}`);
      }

      // Send via unified API client
      const response = await messageApiClient.sendMessage(
        platform,
        account.marketplaceShopId,
        recipientId,
        message,
        requestBody
      );

      console.log(`[sendMarketplaceMessage] ${platform} success:`, response);

      // Return the server-generated message ID
      return {
        messageId: response.messages[0].id,
        status: response.messages[0].status,
        platform,
      };
    } catch (error: any) {
      console.error(`[sendMarketplaceMessage] ${platform} error:`, error);
      return rejectWithValue(error.message);
    }
  }
);
